import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  Account,
  AccountProductEnum,
  AccountTypeEnum,
  TransferOptions,
  TransferTypeEnum,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  router,
  Stack,
  useLocalSearchParams,
} from 'expo-router';
import AccountListBottomSheet from 'features/moveMoney/screens/transfer/AccountListBottomSheet/AccountListBottomSheet';
import { useTransferMoney } from 'features/moveMoney/screens/transfer/hooks/useTransferMoney';
import useHasPlaidMarketingConsent, {
  PlaidMarketingConsentEntryPoint,
} from 'features/plaid/hooks/useHasPlaidMarketingConsent';
import usePlaidLinkLauncher from 'features/plaid/hooks/usePlaidLinkLauncher';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { requestContentCardsRefresh } from 'services/engagement/braze/braze';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import FDICDisclaimer from 'shared/components/FDICDisclaimer/FDICDisclaimer';
import TransferField from 'shared/components/vxl/fields/TransferField/TransferField';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import NumberPad from 'shared/components/vxl/NumberPad/NumberPad';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import ExpressiveXXLText from 'shared/components/vxl/typography/expressive/ExpressiveXXLText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { isMobileBrowser, isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useEngagement } from 'shared/hooks/useBraze';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { applyKeyToAmount } from 'shared/utils/numberUtils';
import { formatDate } from 'shared/utils/string/date';
import useVaroOnboardingFlow from 'shared/utils/useVaroOnboardingFlow/useVaroOnboardingFlow';

export interface TransferInput {
  amount: number;
  currency: string;
  destinationAccountId: string;
  sourceAccountId: string;
}

const TransferMoney = () => {
  const { typeOfAccount, route, flowId, source } = useLocalSearchParams();

  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'transferStrings',
  });

  const bottomSheetRef = useRef<BottomSheetModal>(null);
  const { logCustomEvent } = useEngagement();

  useEffect(() => {
    logCustomEvent('BRAZETRIGGER, transfer success screen viewed');
    requestContentCardsRefresh();
  }, [logCustomEvent]);

  const [transferInput, setTransferInput] = useState<TransferInput>({
    amount: 0,
    currency: 'USD',
    destinationAccountId: '',
    sourceAccountId: '',
  });
  const [transferAmount, setTransferAmount] = useState<string | undefined>(
    '0.00',
  );
  const [selectedFromAccount, setSelectedFromAccount] = useState<Account>();
  const [selectedToAccount, setSelectedToAccount] = useState<Account>();
  const [showingType, setShowingType] = useState<'from' | 'to'>();
  const [plaidErrorMessage, setPlaidErrorMessage] = useState<string>();
  const [transferOptions, setTransferOptions] = useState<TransferOptions>();

  // refreshSourceAccounts is captured by the Plaid launcher result handler, so useTransferMoney must be called first.
  const {
    sourceData,
    destinationData,
    transferEstimateData,
    fetching,
    error,
    refreshSourceAccounts,
    refreshDestinationAccounts,
    transferPossibleError,
  } = useTransferMoney(transferInput);

  const {
    startLaunch,
    cancelLaunch,
    error: plaidError,
  } = usePlaidLinkLauncher(PlaidMarketingConsentEntryPoint.Transfer, {
    onResult: (result) => {
      if (result.status === 'error' || result.status === 'blocked') {
        setPlaidErrorMessage(translatedText('linkAccountFailed'));
        return;
      }

      if (result.status === 'success') {
        refreshSourceAccounts();
        refreshDestinationAccounts();
      }
    },
  });

  useEffect(() => {
    if (plaidError) {
      setPlaidErrorMessage(translatedText('linkAccountError'));
      cancelLaunch();
    }
  }, [cancelLaunch, plaidError, translatedText]);

  const { startPlaidOrShowConsent } = useHasPlaidMarketingConsent({
    entryPoint: PlaidMarketingConsentEntryPoint.Transfer,
  });

  const flow = useVaroOnboardingFlow();

  useEffect(() => {
    setTransferOptions(transferEstimateData);
  }, [transferEstimateData]);

  const selectAccountPressEvent = usePressableAnalytics({
    analyticsId: 'selectAccount',
    customAttributes: {
      direction: !selectedFromAccount ? 'from' : 'to',
    },
  });

  useEffect(() => {
    if (typeOfAccount && !selectedFromAccount && !selectedToAccount) {
      const accountDestination = sourceData?.internal?.find(
        (account) => account?.product === typeOfAccount,
      );
      const accountSource = sourceData?.internal?.find(
        (account) => account?.product === AccountProductEnum.Checking,
      );

      setSelectedFromAccount(accountSource);
      setSelectedToAccount(accountDestination);
      setTransferInput((prevState) => {
        return {
          ...prevState,
          destinationAccountId: accountDestination?.id ?? '',
          sourceAccountId: accountSource?.id ?? '',
        };
      });
    }
  }, [selectedFromAccount, selectedToAccount, sourceData, typeOfAccount]);

  useEffect(() => {
    const displayableExternalSourceAccounts = sourceData?.external?.length;
    if (
      (transferPossibleError?.code === '0' ||
        displayableExternalSourceAccounts === 0) &&
      route === '/onboarding'
    ) {
      startLaunch();
    }
  }, [
    route,
    sourceData?.external?.length,
    startLaunch,
    transferPossibleError?.code,
  ]);

  const handleAccountSelectionAction = (type: 'from' | 'to') => {
    if (type === 'from' || (type === 'to' && selectedFromAccount)) {
      setShowingType(type);
      bottomSheetRef?.current?.present();
      cancelLaunch();
    }
  };

  const transferAmountValue = parseFloat(transferAmount ?? '0');
  const maxTransferAmount = Number(transferOptions?.maxTransfer?.amount ?? 0);
  const isTransferAmountValid =
    !Number.isNaN(transferAmountValue) &&
    transferAmountValue > 0 &&
    transferAmountValue <= maxTransferAmount;
  const hasExceededTransferLimit = transferAmountValue > maxTransferAmount;
  const areTransferAccountsSelected =
    selectedFromAccount !== undefined && selectedToAccount !== undefined;

  const handleNumPadAction = (value: string) => {
    const newAmount = applyKeyToAmount(transferAmount ?? '', value);
    if (newAmount !== undefined && newAmount !== transferAmount) {
      setTransferAmount(newAmount);
    }
  };

  const nextButtonCustomAttributes = {
    from_account_id: selectedFromAccount?.id ?? '',
    to_account_id: selectedToAccount?.id ?? '',
    from_account_type: (
      selectedFromAccount?.[
        selectedFromAccount.type === AccountTypeEnum.Internal
          ? 'product'
          : 'type'
      ] || ''
    ).toLowerCase(),
    to_account_type: (
      selectedToAccount?.[
        selectedToAccount.type === AccountTypeEnum.Internal ? 'product' : 'type'
      ] || ''
    ).toLowerCase(),
    from_bank: selectedFromAccount?.institution?.name?.toLowerCase() ?? '',
    to_bank: selectedToAccount?.institution?.name?.toLowerCase() ?? '',
    transfer_amount: transferAmount ?? '',
    transfer_type: (
      (transferEstimateData?.type === TransferTypeEnum.InternalTransfer
        ? 'internal'
        : transferEstimateData?.type) || ''
    )?.toLowerCase(),
  };

  const handleNextAction = () => {
    router.push({
      pathname: `/${flow}/transfer/confirm-transfer`,
      params: {
        amount: transferAmount,
        currency: selectedFromAccount?.availableBalance?.currency ?? 'USD',
        destinationAccountId: selectedToAccount?.id,
        sourceAccountId: selectedFromAccount?.id,
        sourceAccountName: selectedFromAccount?.nickname,
        destinationAccountName: selectedToAccount?.nickname,
        date: formatDate(new Date()),
        transferEstimate: transferEstimateData?.transferEstimate,
        fromAccountType: nextButtonCustomAttributes.from_account_type,
        toAccountType: nextButtonCustomAttributes.to_account_type,
        fromBank: nextButtonCustomAttributes.from_bank,
        toBank: nextButtonCustomAttributes.to_bank,
        transferType: nextButtonCustomAttributes.transfer_type,
        route,
        fromAccountId: selectedFromAccount?.id,
        toAccountId: selectedToAccount?.id,
        ...(flowId && { flowId }),
        ...(source && { source }),
      },
    });
  };
  const { defaultTitle, defaultMessage, cancelButtonTitle } =
    useDefaultErrorStrings();

  const buttons = {
    primaryButton: {
      title: cancelButtonTitle,
      onPress: () => {
        router.back();
      },
    },
  };

  const errorModel = error?.message
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: defaultTitle,
        messageBody: error.message ?? defaultMessage,
        ...buttons,
      }
    : null;

  const handleIconPress = () => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/');
    }
  };

  const handleLinkAccountPress = () => {
    setPlaidErrorMessage(undefined);
    cancelLaunch();
    startPlaidOrShowConsent(startLaunch);
  };

  const handleDismissPlaidError = () => {
    setPlaidErrorMessage(undefined);
    cancelLaunch();
  };

  return (
    <>
      <Stack.Screen
        options={{
          title: translatedText('title'),
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={
                typeOfAccount === AccountProductEnum.ChargeCard
                  ? handleIconPress
                  : undefined
              }
            />
          ),
        }}
      />
      {typeOfAccount ? (
        <ScreenTransitionOverlay
          isLoading={!selectedFromAccount && !selectedToAccount}
          LoadingComponent={<LoadingSpinner />}
          ErrorComponent={
            errorModel && !fetching ? <ErrorScreen {...errorModel} /> : null
          }
        />
      ) : null}
      <ScrollView contentContainerStyle={styles.scrollStyle}>
        <TransferField
          primaryText={
            selectedFromAccount
              ? `${selectedFromAccount.nickname} • ${selectedFromAccount.accountNumberLast4}`
              : translatedText('selectAnAccount')
          }
          secondaryText={
            selectedFromAccount
              ? `${translatedText('available')} ${selectedFromAccount.availableBalance?.formatted}`
              : ''
          }
          labelText={translatedText('from')}
          avatarAccessory={
            selectedFromAccount
              ? {
                  name: VXLComponentId.ICON_AVATAR,
                  id: 'transfer-money-source-varo-logo',
                  props: {
                    iconAccessibilityLabel: 'logo',
                    imageSource: { uri: selectedFromAccount.iconUrl },
                    variant: 'MD',
                  },
                }
              : {
                  name: VXLComponentId.ICON_AVATAR,
                  id: 'transfer-money-source-bank-icon',
                  props: {
                    iconAccessibilityLabel: 'bank-icon',
                    iconKey: 'BANK',
                    variant: 'MD',
                  },
                }
          }
          onPress={() => (
            selectAccountPressEvent(),
            refreshSourceAccounts(),
            handleAccountSelectionAction('from')
          )}
        />
        <TransferField
          primaryText={
            selectedToAccount
              ? `${selectedToAccount.nickname} • ${selectedToAccount.accountNumberLast4}`
              : translatedText('selectAnAccount')
          }
          secondaryText={
            selectedToAccount
              ? `${translatedText('available')} ${selectedToAccount.availableBalance?.formatted}`
              : ''
          }
          labelText={translatedText('to')}
          avatarAccessory={
            selectedToAccount
              ? {
                  name: VXLComponentId.ICON_AVATAR,
                  id: 'transfer-money-destination-varo-logo',
                  props: {
                    iconAccessibilityLabel: 'logo',
                    imageSource: { uri: selectedToAccount.iconUrl },
                    variant: 'MD',
                  },
                }
              : {
                  name: VXLComponentId.ICON_AVATAR,
                  id: 'transfer-money-destination-bank-icon',
                  props: {
                    iconAccessibilityLabel: 'bank-icon',
                    iconKey: 'BANK',
                    variant: 'MD',
                  },
                }
          }
          onPress={() => (
            selectedFromAccount && selectAccountPressEvent(),
            selectedFromAccount && refreshDestinationAccounts(),
            handleAccountSelectionAction('to')
          )}
        />
        <View
          style={
            isMobileBrowser
              ? styles.amountMainWrapperMobileWeb
              : styles.amountMainWrapper
          }
        >
          {transferAmount ? (
            <AmplitudeMask style={styles.amplitudeMaskStyle}>
              <ExpressiveXXLText
                numberOfLines={0}
                style={styles.amountTextStyle}
              >
                {`$` + transferAmount}
              </ExpressiveXXLText>
            </AmplitudeMask>
          ) : null}
        </View>

        <View style={styles.footerContainer}>
          <View style={styles.transferLimitContainer}>
            <BodyXSText
              numberOfLines={0}
              style={[
                styles.transferLimitTextStyle,
                {
                  color: hasExceededTransferLimit
                    ? theme.color.status.negative
                    : theme.color.status.info,
                },
              ]}
            >
              {transferOptions
                ? translatedText('transferLimit') +
                  (transferOptions.maxTransfer?.formatted ??
                    translatedText('missingMaxTransferFallback'))
                : null}
            </BodyXSText>
            {selectedToAccount ? (
              <BodyXSText
                style={styles.transferEstimateTextStyle}
                numberOfLines={1}
              >
                {transferEstimateData?.transferEstimate}
              </BodyXSText>
            ) : null}
          </View>
          <FDICDisclaimer />
        </View>
      </ScrollView>

      <View style={styles.numPadContainer}>
        <NumberPad
          submitButtonLabel={translatedText('button')}
          handleButtonPress={handleNumPadAction}
          handleSubmit={handleNextAction}
          disabled={!areTransferAccountsSelected}
          submitDisabled={!isTransferAmountValid}
          analyticsId="transferMoneyNextButton"
          customAttributes={nextButtonCustomAttributes}
        />
      </View>
      <AccountListBottomSheet
        bottomSheetRef={bottomSheetRef}
        fetching={fetching}
        showingType={showingType}
        setSelectedFromAccount={setSelectedFromAccount}
        setSelectedToAccount={setSelectedToAccount}
        setTransferInput={setTransferInput}
        sourceData={sourceData}
        destinationData={destinationData}
        refreshSourceAccounts={refreshSourceAccounts}
        refreshDestinationAccounts={refreshDestinationAccounts}
        onLinkAccountPress={handleLinkAccountPress}
        setTransferOptions={setTransferOptions}
      />
      {plaidErrorMessage ? (
        <Snackbar
          text={plaidErrorMessage}
          buttonOnPress={handleDismissPlaidError}
          disableAutoHide={true}
        />
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  amountMainWrapper: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    paddingBottom: isWeb ? theme.spacing.m : 0,
  },
  amountMainWrapperMobileWeb: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  amountTextStyle: {
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.xxl,
    textAlign: 'center',
  },
  amplitudeMaskStyle: {
    display: 'flex',
  },
  footerContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
  },
  numPadContainer: {
    backgroundColor: theme.color.background.primary,
    padding: theme.spacing.m,
    width: '100%',
  },
  scrollStyle: {
    backgroundColor: theme.color.background.primary,
    flexGrow: 1,
    justifyContent: 'space-between',
  },
  transferEstimateTextStyle: {
    color: theme.color.text.secondary,
  },
  transferLimitContainer: {
    paddingBottom: theme.spacing.s,
    justifyContent: 'center',
    marginHorizontal: theme.spacing.m,
  },
  transferLimitTextStyle: { flexShrink: 1 },
});

export default TransferMoney;
